import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from "@mui/material";
import { format } from "date-fns";
import React, { useEffect, useRef, useState } from "react";

type PaymentDateUser = {
  id: number;
  nome: string;
  nextPaymentDate?: Date | string | null;
};

type PaymentDateDialogProps = {
  user: PaymentDateUser | null;
  loading?: boolean;
  onClose: () => void;
  onSave: (date: string | null) => void | Promise<void>;
};

const toInputDate = (date?: Date | string | null) =>
  date ? new Date(date).toISOString().slice(0, 10) : "";

const PaymentDateDialog: React.FC<PaymentDateDialogProps> = ({
  user,
  loading = false,
  onClose,
  onSave,
}) => {
  const [paymentDate, setPaymentDate] = useState("");
  const [isPaymentExempt, setIsPaymentExempt] = useState(false);
  const paymentDateInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setPaymentDate(toInputDate(user?.nextPaymentDate));
    setIsPaymentExempt(
      !!user &&
        (user.nextPaymentDate === null || user.nextPaymentDate === undefined),
    );
  }, [user]);

  const handlePaymentExemptChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const isExempt = event.target.checked;
    setIsPaymentExempt(isExempt);

    if (isExempt) {
      setPaymentDate("");
      return;
    }

    requestAnimationFrame(() => paymentDateInputRef.current?.focus());
  };

  const extendPaymentDate = (days: number) => {
    const currentPaymentDate = user?.nextPaymentDate
      ? new Date(user.nextPaymentDate)
      : new Date();
    const today = new Date();
    const baseDate = currentPaymentDate > today ? currentPaymentDate : today;

    baseDate.setUTCDate(baseDate.getUTCDate() + days);
    console.log("Extended payment date:", baseDate.toISOString().slice(0, 10));
    setPaymentDate(baseDate.toISOString().slice(0, 10));
  };

  return (
    <Dialog
      open={!!user}
      onClose={() => !loading && onClose()}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Alterar próximo pagamento</DialogTitle>
      <DialogContent>
        <p style={{ marginTop: 8, marginBottom: 4 }}>
          Usuário: <strong>{user?.nome}</strong>
        </p>
        <p style={{ marginBottom: 16, color: "#666" }}>
          Data atual:{" "}
          <strong>
            {user?.nextPaymentDate
              ? format(new Date(user.nextPaymentDate), "dd/MM/yyyy")
              : "Usuário isento"}
          </strong>
        </p>

        <FormControlLabel
          control={
            <Checkbox
              checked={isPaymentExempt}
              onChange={handlePaymentExemptChange}
              disabled={loading}
              color="default"
            />
          }
          label="Isento de pagamento"
          sx={{ marginBottom: 1 }}
        />

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 8,
            marginBottom: 16,
          }}
        >
          {[30, 60, 90].map(days => (
            <Button
              key={days}
              variant="outlined"
              color="inherit"
              onClick={() => extendPaymentDate(days)}
              disabled={loading || isPaymentExempt}
            >
              +{days} dias
            </Button>
          ))}
        </div>

        <TextField
          fullWidth
          type="date"
          label="Nova data"
          value={paymentDate}
          onChange={event => setPaymentDate(event.target.value)}
          disabled={loading || isPaymentExempt}
          inputRef={paymentDateInputRef}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: new Date().toISOString().slice(0, 10) }}
        />
        <small style={{ display: "block", marginTop: 10, color: "#666" }}>
          {isPaymentExempt
            ? "Este usuário não terá uma próxima data de pagamento."
            : "Os atalhos acrescentam dias à data cadastrada. Se ela estiver vencida ou não existir, a contagem começa hoje."}
        </small>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="error" disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => onSave(isPaymentExempt ? null : paymentDate)}
          disabled={loading || (!isPaymentExempt && !paymentDate)}
        >
          {isPaymentExempt ? "Salvar isenção" : "Salvar data"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentDateDialog;
